import { jsx as _jsx } from "react/jsx-runtime";
import React from 'react';
import { useModalsDialogsContext } from './modals-dialogs-context';
/**
 * The hook to render a custom dialog.
 *
 * Allows passing a component in as `children`, which will be rendered when opening
 * the dialog.
 *
 * In most cases, you should go for the `useGenericDialog` version, as dialogs are supposed
 * to be only text, images and a confirm and cancel button.
 *
 * @see {@link file://./use-generic-dialog.tsx}
 * @param options see {@link DialogOptions}
 * @returns an object containing functions to show and hide the dialog
 */
export function useDialog(options) {
    const { showModalDialog, hideModalDialog } = useModalsDialogsContext();
    const id = React.useId();
    const show = React.useCallback((props) => {
        var _a;
        const { onClose } = props;
        // onClose calls the context to remove the dialog from the stack
        const handleClose = () => {
            hideModalDialog(id);
            onClose === null || onClose === void 0 ? void 0 : onClose();
        };
        /*
        // Render the dialog content passing the props and injected onClose
        const element = options.children({...props, onClose: handleClose});
        showModalDialog({id, type: 'dialog', element}); */
        const Lazy = React.lazy(options.lazyChildren);
        showModalDialog({
            id,
            type: 'dialog',
            onClose: handleClose,
            element: (_jsx(React.Suspense, { fallback: (_a = options.skeleton) !== null && _a !== void 0 ? _a : null, children: _jsx(Lazy, { ...props, onClose: handleClose }) })),
        });
    }, [hideModalDialog, id, options, showModalDialog]);
    return React.useMemo(() => ({
        showDialog: show,
        hideDialog: () => hideModalDialog(id),
    }), [hideModalDialog, show, id]);
}
